import React, { useEffect, useRef, useState } from 'react';

/** Mouse / touch / pen drawing surface for a hand-drawn signature. */
export default function SignaturePad({ onChange, height = 150, disabled = false }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const width = c.parentElement ? c.parentElement.clientWidth : 500;
    c.width = width * ratio;
    c.height = height * ratio;
    c.style.width = `${width}px`;
    c.style.height = `${height}px`;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
  }, [height]);

  const point = (ev) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: ev.clientX - r.left, y: ev.clientY - r.top };
  };

  const start = (ev) => {
    if (disabled) return;
    ev.preventDefault();
    canvasRef.current.setPointerCapture(ev.pointerId);
    drawing.current = true;
    last.current = point(ev);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.arc(last.current.x, last.current.y, 1, 0, Math.PI * 2);
    ctx.fill();
  };

  const move = (ev) => {
    if (!drawing.current) return;
    ev.preventDefault();
    const p = point(ev);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) setEmpty(false);
  };

  const end = (ev) => {
    if (!drawing.current) return;
    drawing.current = false;
    try { canvasRef.current.releasePointerCapture(ev.pointerId); } catch (_) { /* already released */ }
    if (onChange) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.restore();
    setEmpty(true);
    if (onChange) onChange(null);
  };

  return (
    <div className="eng-sigpad">
      <canvas
        ref={canvasRef}
        className="eng-sigpad-canvas"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
        aria-label="Draw your signature"
      />
      <div className="eng-sigpad-bar">
        <span className="eng-help">{empty ? 'Draw your signature above with your mouse, finger or pen.' : 'Signature captured.'}</span>
        <button type="button" className="eng-btn-ghost eng-btn-sm" onClick={clear} disabled={empty || disabled}>
          <i className="fas fa-eraser" /> Clear
        </button>
      </div>
    </div>
  );
}
